import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { injectDispatch } from '@ngrx/signals/events';
import {
  InputLabelComponent,
  SelectInputComponent,
  NumberInputComponent,
  TextInputComponent,
  IconComponent,
} from '@navstation-portal/shared/ui/components';
import { IconNames } from '@navstation-portal/shared/data-model/ui';
import { Button } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { FuelSpecsStore } from '@navstation-portal/fuel-specs/data-access/store';
import { ISO_FUEL_GRADE_MAP } from '@navstation-portal/fuel-specs/data-model/fuel-spec';
import {
  offshoreBunkerFormEvents,
  offshoreBunkerEvents,
  OffshoreBunkerFormStore,
  AbstractStoreConnectedSectionComponent,
} from '@navstation-portal/reporting/data-access/offshore-report';
import { FuelOilBunkersComponent } from './fuel-oil-bunkers/fuel-oil-bunkers.component';

@Component({
  selector: 're-fuel-oil-bunker-table',
  templateUrl: './fuel-oil-bunker-table.component.html',
  styleUrl: './fuel-oil-bunker-table.component.css',
  imports: [
    FormsModule,
    InputLabelComponent,
    SelectInputComponent,
    NumberInputComponent,
    TextInputComponent,
    Button,
    InputTextModule,
    TooltipModule,
    ToggleButtonModule,
    FuelOilBunkersComponent,
    IconComponent,
  ],
})
export class FuelOilBunkerTableComponent extends AbstractStoreConnectedSectionComponent {
  IconNames = IconNames;

  enabled = false;
  tabId = 'bunker';
  sectionId = 'fuelOilBunkerTable';
  events = injectDispatch(offshoreBunkerFormEvents);
  bunkerEvents = injectDispatch(offshoreBunkerEvents);
  offshoreBunkerStore = inject(OffshoreBunkerFormStore);
  fuelSpecsStore = inject(FuelSpecsStore);

  HEADINGS = [
    'Fuel Type',
    'ISO Fuel Grade',
    'Quantity Ordered',
    'Quantity Received',
    'BDN Figures',
    'Density at 15°C (kg/m3)',
    'LCV (kJ/kg)',
    'Sulphur (%)',
    'Viscosity at 40°C (cSt)',
    'Sulphur (%) Lab Analysis',
  ] as const;

  FIELDS = [
    'fuelType',
    'isoFuelGrade',
    'quantityOrdered',
    'quantityReceived',
    'bdnFigures',
    'density',
    'lcv',
    'sulphur',
    'viscosity',
    'labSulphur',
  ] as const;

  NUMBER_FIELDS = [
    'quantityOrdered',
    'quantityReceived',
    'density',
    'lcv',
    'sulphur',
    'viscosity',
    'labSulphur',
  ] as const;

  isRequired(field: (typeof this.FIELDS)[number]) {
    const REQUIRED_FIELDS: string[] = [
      'quantityReceived',
      'bdnFigures',
      'density',
      'lcv',
      'sulphur',
      'viscosity',
    ] as const;
    return REQUIRED_FIELDS.includes(field);
  }

  getFuelTypeCode(value?: SelectOption): string | undefined {
    return (value as SelectOption | undefined)?.code as string | undefined;
  }

  getIsoGradeOptions(fuelType?: SelectOption): SelectOption[] {
    if (!fuelType) return [];
    const code = this.getFuelTypeCode(fuelType);

    const ISO_GRADE_OPTIONS_MAP: Record<string, SelectOption[]> =
      Object.fromEntries(
        Object.entries(ISO_FUEL_GRADE_MAP).map(([key, grades]) => [
          key,
          grades.map((item) => ({
            name: item.toLocaleUpperCase(),
            code: item,
          })),
        ]),
      );

    return (code && ISO_GRADE_OPTIONS_MAP[code]) || [];
  }

  onFuelISOGradeChange(
    index: number,
    fuelType: SelectOption | undefined,
    isoGrade: SelectOption | undefined,
  ) {
    const lookedUpValues =
      this.fuelSpecsStore.getFuelSpecValues()(fuelType, isoGrade) ?? {};

    this.bunkerEvents.updateBunkerRow({
      rowIndex: index,
      ...lookedUpValues,
    });
  }

  FIELD_TYPE_MAP = {
    fuelType: 'text',
    isoFuelGrade: 'text',
    quantityOrdered: 'number',
    quantityReceived: 'number',
    bdnFigures: 'text',
    density: 'number',
    lcv: 'number',
    sulphur: 'number',
    viscosity: 'number',
    labSulphur: 'number',
  };

  asString = (string: unknown): string => {
    return string as string;
  };

  asDate = (date: unknown): Date => {
    return date as Date;
  };

  removeBunkerRow(index: number) {
    this.bunkerEvents.removeBunkerRow({ rowIndex: index });
  }

  addBunkerRow() {
    this.bunkerEvents.addBunkerRow();
  }

  onFuelTypeCleared(index: number) {
    this.bunkerEvents.clearBunkerRow({
      rowIndex: index,
      omitFields: ['isoFuelGrade'],
    });
  }

  onFuelTypeChange(value: SelectOption | undefined, index: number) {
    if (!value) {
      this.bunkerEvents.clearBunkerRow({
        rowIndex: index,
        omitFields: ['fuelType'],
      });
    }
  }

  clearBunkerRow(index: number, omitFields: string[] = []) {
    this.bunkerEvents.clearBunkerRow({ rowIndex: index, omitFields });
  }
}
